import React from 'react';

import PropTypes from 'prop-types';
import { Box, StackView, Typography } from '@telus-uds/components-web';

import {
  Thead,
  Td,
  Tr,
  Th
} from '../orderDetailsSection/ratePlans/components/css';
import getFormattedPricing from '../orderDetailsSection/utils';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';

const ServicesHardware = ({ serviceData, serviceName, productSelected }) => {
  if (!serviceData.length) return null;

  const {
    contentfulValues: { valueNorthStarSystemsFarmAndRanch }
  } = useLocalizedStringsContext();

  return (
    <table>
      <Thead>
        <Tr>
          <Th width="50%">{serviceName}</Th>
          {productSelected !== valueNorthStarSystemsFarmAndRanch && (
            <>
              <Th width="10%">Rate Plan Units</Th>
              <Th width="10%">MTM Units</Th>
            </>
          )}

          <Th width="10%">Total Units</Th>
          <Th width="10%">Unit Price</Th>
          <Th width="10%">Total</Th>
        </Tr>
      </Thead>
      <tbody>
        {serviceData.map(data => {
          const {
            name,
            includedQuantity,
            additionalQuantity,
            unitPrice
          } = data;
          return (
            <Tr>
              <Td style={{ background: '#f7f7f7', fontWeight: '700' }}>
                {name}
              </Td>
              {productSelected !== valueNorthStarSystemsFarmAndRanch && (
                <>
                  <Td>{includedQuantity}</Td>
                  <Td>{additionalQuantity}</Td>
                </>
              )}
              <Td>{includedQuantity + additionalQuantity}</Td>
              <Td>{getFormattedPricing(unitPrice)}</Td>
              <Td>{getFormattedPricing(unitPrice * additionalQuantity)}</Td>
            </Tr>
          );
        })}
      </tbody>
    </table>
  );
};

ServicesHardware.propTypes = {
  serviceName: PropTypes.string.isRequired,
  productSelected: PropTypes.string.isRequired,
  serviceData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      device: PropTypes.string,
      term: PropTypes.string,
      quantity: PropTypes.number,
      unitPrice: PropTypes.number,
      fiftyPercentInUS: PropTypes.bool
    })
  ).isRequired
};

const ServicesAccessory = ({ serviceData, serviceName }) => {
  if (!serviceData.length) return null;

  return (
    <table>
      <Thead>
        <Tr>
          <Th width="50%">{serviceName}</Th>
          <Th width="10%">Quantity</Th>
          <Th width="10%">Unit Price</Th>
          <Th width="10%">Total</Th>
        </Tr>
      </Thead>
      <tbody>
        {serviceData.map(data => {
          const { name, additionalQuantity, unitPrice } = data;
          return (
            <Tr>
              <Td style={{ background: '#f7f7f7', fontWeight: '700' }}>
                {name}
              </Td>
              <Td>{additionalQuantity}</Td>
              <Td>{getFormattedPricing(unitPrice)}</Td>
              <Td>{getFormattedPricing(unitPrice * additionalQuantity)}</Td>
            </Tr>
          );
        })}
      </tbody>
    </table>
  );
};

ServicesAccessory.propTypes = {
  serviceName: PropTypes.string.isRequired,
  serviceData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      device: PropTypes.string,
      term: PropTypes.string,
      quantity: PropTypes.number,
      unitPrice: PropTypes.number,
      fiftyPercentInUS: PropTypes.bool
    })
  ).isRequired
};

const SummaryHardware = ({ orderData, total }) => {
  const {
    contentfulValues: { valueGeotab, valueGeoforce }
  } = useLocalizedStringsContext();
  const {
    hardwareList,
    accessoryList,
    ravenAccessoryList = [],
    iotProductSelected
  } = orderData;
  const { hardware, accessories } = total;

  const getAccessoryServiceName = () => {
    if (iotProductSelected === valueGeotab && ravenAccessoryList.length > 0) {
      return 'Geotab Accessories';
    }
    if (iotProductSelected === valueGeoforce && ravenAccessoryList.length > 0) {
      return 'Geoforce Accessories';
    }
    return 'Accessories';
  };

  return (
    <>
      {(hardware > 0 || accessories > 0) && (
        <StackView space={3}>
          <Typography heading="h4" variant={{ size: 'h4' }} block>Hardware</Typography>
          <Box>
            <ServicesHardware
              serviceData={hardwareList}
              serviceName="Hardware"
              productSelected={iotProductSelected}
            />
            <ServicesAccessory
              serviceData={accessoryList}
              serviceName={getAccessoryServiceName()}
            />
            <ServicesAccessory
              serviceData={ravenAccessoryList}
              serviceName="Raven Accessories"
            />
            <div
              style={{
                background: '#f4f4f4',
                border: '1px solid #e3e3e3',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1rem'
              }}
            >
              <p style={{ marginLeft: '16rem', fontWeight: '600' }}>Total</p>
              <p style={{ marginRight: '0.65rem', fontWeight: '600' }}>
                {getFormattedPricing(hardware + accessories)}
              </p>
            </div>
          </Box>
        </StackView>
      )}
    </>
  );
};

SummaryHardware.propTypes = {
  orderData: PropTypes.shape({
    hardwareList: PropTypes.arrayOf(PropTypes.shape({})),
    accessoryList: PropTypes.arrayOf(PropTypes.shape({})),
    ravenAccessoryList: PropTypes.arrayOf(PropTypes.shape({})),
    iotProductSelected: PropTypes.bool
  }).isRequired,
  total: PropTypes.shape({
    hardware: PropTypes.number,
    accessories: PropTypes.number
  }).isRequired
};

export default SummaryHardware;
